// Admin endpoints (JWT bearer). Shapes mirror api/src/http/routes/admin.ts.
import { z } from "zod";
import { apiFetch } from "./client";
import type { TierId } from "./auth";

export interface UsageReport {
  tier: TierId;
  // Asymmetry is intentional (mirrors api usage-service): channels.used is a
  // control-plane DB count(*) so it's ALWAYS metered; agents.used comes from the
  // relay and is null when the relay link is off (RELAY_URL/token unset).
  agents: { used: number | null; limit: number; remaining: number | null };
  channels: { used: number; limit: number; remaining: number };
  // Stripe subscription state for the dunning banner + Billing Portal (Phase 2).
  // Null when the org has never subscribed (free).
  subscription: { status: string | null; currentPeriodEnd: string | null };
}

// Validated at the boundary (vault Type_Assertion): envelope drift becomes a
// ContractError → the Dashboard's isError Callout, not an undefined-deref crash.
// agents.used/remaining are .nullable() to mirror the API exactly (relay-off → null).
const usageReportSchema = z.object({
  tier: z.enum(["free", "small", "medium", "large", "xlarge"]),
  agents: z.object({ used: z.number().nullable(), limit: z.number(), remaining: z.number().nullable() }),
  channels: z.object({ used: z.number(), limit: z.number(), remaining: z.number() }),
  subscription: z.object({ status: z.string().nullable(), currentPeriodEnd: z.string().nullable() }),
}) satisfies z.ZodType<UsageReport>;

export function getUsage(signal?: AbortSignal): Promise<UsageReport> {
  return apiFetch("/admin/usage", { auth: true, signal, schema: usageReportSchema });
}
